/**
 * Location Select Component
 * 
 * Two-level dropdown for selecting province and city (kabupaten).
 */

import * as React from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { locationService } from "@/services/locationService";

interface LocationSelectProps {
  province?: string;
  city?: string;
  onProvinceChange?: (province: string | undefined) => void;
  onCityChange?: (city: string | undefined) => void;
  disabled?: boolean;
  showLabels?: boolean;
  allowedProvinces?: string[];
  /** When true, province cannot be changed (e.g. single wilayah admin). */
  provinceLocked?: boolean;
  allowedCities?: string[];
  /** When true, kabupaten/kota cannot be changed (e.g. admin kota/kab). */
  cityLocked?: boolean;
}

export function LocationSelect({
  province,
  city,
  onProvinceChange,
  onCityChange,
  disabled = false,
  showLabels = true,
  allowedProvinces,
  provinceLocked = false,
  allowedCities,
  cityLocked = false,
}: LocationSelectProps) {
  const [allProvinces, setAllProvinces] = React.useState<string[]>([]);
  const [cities, setCities] = React.useState<string[]>([]);
  const [loadingProvinces, setLoadingProvinces] = React.useState(false);
  const [loadingCities, setLoadingCities] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Filter provinces based on allowedProvinces
  const provinces = React.useMemo(() => {
    if (!allowedProvinces || allowedProvinces.length === 0) {
      return allProvinces;
    }
    return allProvinces.filter((p) => allowedProvinces.includes(p));
  }, [allProvinces, allowedProvinces]);

  const cityOptions = React.useMemo(() => {
    if (!allowedCities || allowedCities.length === 0) {
      return cities;
    }
    return cities.filter((c) => allowedCities.includes(c));
  }, [cities, allowedCities]);

  // Fetch provinces on mount
  React.useEffect(() => {
    const fetchProvinces = async () => {
      setLoadingProvinces(true);
      setError(null);
      try {
        const data = await locationService.fetchProvinces();
        setAllProvinces(data);
      } catch (err) {
        console.error("Error fetching provinces:", err);
        setError(err instanceof Error ? err.message : "Failed to load provinces");
      } finally {
        setLoadingProvinces(false);
      }
    };

    fetchProvinces();
  }, []);

  // Fetch cities when province changes
  React.useEffect(() => {
    if (!province) {
      setCities([]);
      return;
    }

    const fetchCities = async () => {
      setLoadingCities(true);
      setError(null);
      try {
        const data = await locationService.fetchCitiesByProvince(province);
        setCities(data);
        
        // Clear city selection if current city is not in the new list
        if (city && !data.includes(city)) {
          onCityChange?.(undefined);
        }
      } catch (err) {
        console.error("Error fetching cities:", err);
        setError(err instanceof Error ? err.message : "Failed to load cities");
        setCities([]);
      } finally {
        setLoadingCities(false);
      }
    };

    fetchCities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [province]); // Only refetch when province changes

  return (
    <div className="space-y-4">
      {/* Province Selection */}
      <div className="space-y-2">
        {showLabels && (
          <Label htmlFor="province-select">
            Provinsi
            {provinceLocked && (
              <span className="ml-2 font-normal text-muted-foreground">(wilayah admin)</span>
            )}
          </Label>
        )}
        <Select
          value={province}
          onValueChange={(value) => {
            const newProvince = value === "no-data" || value === "" ? undefined : value;
            // Call onProvinceChange first, which should handle clearing the city
            onProvinceChange?.(newProvince);
            // Clear city when province changes - but only if province actually changed
            if (newProvince !== province) {
              onCityChange?.(undefined);
            }
          }}
          disabled={disabled || loadingProvinces || provinceLocked}
        >
          <SelectTrigger id="province-select" className="w-full">
            {loadingProvinces ? (
              <div className="flex items-center">
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                <span>Memuat provinsi...</span>
              </div>
            ) : (
              <SelectValue placeholder="Pilih Provinsi" />
            )}
          </SelectTrigger>
          <SelectContent>
            {provinces.length === 0 && !loadingProvinces ? (
              <SelectItem value="no-data" disabled>
                Tidak ada provinsi
              </SelectItem>
            ) : (
              provinces.map((p) => (
                <SelectItem key={p} value={p}>
                  {p}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      </div>

      {/* City Selection */}
      {province && (
        <div className="space-y-2">
          {showLabels && (
            <Label htmlFor="city-select">
              Kabupaten/Kota
              {cityLocked && (
                <span className="ml-2 font-normal text-muted-foreground">(wilayah admin)</span>
              )}
            </Label>
          )}
          <Select
            value={city}
            onValueChange={(value) => {
              onCityChange?.(value === "no-data" || value === "" ? undefined : value || undefined);
            }}
            disabled={disabled || loadingCities || !province || cityLocked}
          >
            <SelectTrigger id="city-select" className="w-full">
              {loadingCities ? (
                <div className="flex items-center">
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  <span>Memuat kota...</span>
                </div>
              ) : (
                <SelectValue placeholder="Pilih Kabupaten/Kota" />
              )}
            </SelectTrigger>
            <SelectContent>
              {cityOptions.length === 0 && !loadingCities ? (
                <SelectItem value="no-data" disabled>
                  Tidak ada kota
                </SelectItem>
              ) : (
                cityOptions.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>
      )}

      {error && (
        <div className="text-sm text-red-600 dark:text-red-400">
          {error}
        </div>
      )}
    </div>
  );
}

